<template>
  <div class="py-8 px-6">
    <CalendarAdminPlusScheduleClassDialog ref="scheduleClassDialog" @saved="onScheduleClassDialogSaved" />
    <ViewCalendarHeader @add-class="openCalendarClassDialog"
      :disabled-actions="loadingInitial" />


    <div v-if="loadingInitial" class="d-flex align-center justify-center" style="min-height: 70vh;">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <div v-else-if="isEmptyState" class="d-flex align-center justify-center" style="min-height: 70vh;">

      <v-row class="d-flex align-center justify-center">
        <v-col class="d-flex flex-column align-center justify-center" cols="12">
          <v-img class="mb-6" height="130" :src="require('@/assets/icons/admin+/academy_calendar.svg')" contain></v-img>
          <div class="text-center mb-6">
            <span class="font-roboto text-empty">
              {{ $t('message.calendar_admin_plus.calendar_index_empty_title') }}
            </span>
            <div class="text-body-1 text-empty">
              {{ $t('message.calendar_admin_plus.calendar_index_empty_subtitle') }}
            </div>
          </div>

          <PlusButton small intent="primary" v-allowed="'edit_schedule'" @click="openCalendarClassDialog()">
            <span class="font-roboto text-button d-inline">{{
              $t('message.calendar_admin_plus.calendar_index_add_class') }}</span>
          </PlusButton>

        </v-col>
      </v-row>
    </div>

    <v-tabs v-else v-model="activeTab" show-arrows slider-color="plusIndigoDarken1" class="mt-6 font-roboto">
      <v-tab :key="'calendar'" ripple @click="clickTab(0)"
        class="d-flex align-center font-roboto font-weight-medium text-body-2"
        :class="activeTab === 0 ? 'plusIndigoDarken1--text' : ''">{{ $t('message.calendar_admin_plus.calendar')
        }}</v-tab>
      <v-tab :key="'public_events'" ripple @click="clickTab(1)"
        class="d-flex align-center font-roboto font-weight-medium text-body-2"
        :class="activeTab === 1 ? 'plusIndigoDarken1--text' : ''">{{ $t('message.calendar_admin_plus.calendar_tab_events')
        }}</v-tab>
      <v-tab :key="'public_schedule'" ripple @click="clickTab(2)"
        class="d-flex align-center font-roboto font-weight-medium text-body-2"
        :class="activeTab === 2 ? 'plusIndigoDarken1--text' : ''">{{ $t('message.calendar_admin_plus.schedule')
        }}</v-tab>
      <v-tab :key="'configuration'" ripple @click="clickTab(3)" v-allowed="'edit_calendar'"
        class="d-flex align-center font-roboto font-weight-medium text-body-2"
        :class="activeTab === 3 ? 'plusIndigoDarken1--text' : ''">{{ $t('message.calendar_admin_plus.calendar_tab_settings')
        }}</v-tab>

      <v-tabs-items v-model="activeTab">
        <v-tab-item :key="'calendar'" class="mt-10">
          <CalendarTab ref="calendarTab" v-if="activeTab === 0 || calendarLoaded" :active-tab="activeTab"
            @calendar-loaded="calendarLoaded = true" />
        </v-tab-item>
        <v-tab-item :key="'public_events'" class="mt-10">
          <AcademyEventsTab v-if="activeTab === 1 || academyEventsLoaded" :active-tab="activeTab"
            @loaded="academyEventsLoaded = true" />
        </v-tab-item>
        <v-tab-item :key="'public_schedule'" class="mt-10" v-allowed="'edit_calendar'">
          <ScheduleTab ref="scheduleTab" v-if="activeTab === 2 || scheduleLoaded" :active-tab="activeTab"
            @loaded="scheduleLoaded = true" />
        </v-tab-item>
        <v-tab-item :key="'configuration'" class="mt-10" v-allowed="'edit_calendar'">
          <ConfigurationTab v-if="activeTab === 3 || configLoaded" :active-tab="activeTab"
            @loaded="configLoaded = true" />
        </v-tab-item>
      </v-tabs-items>
    </v-tabs>
    <CustomOnboarding ref="calendarAdminPlusTour" tour-name="calendar-adminplus-onboarding"
      :steps="calendarAdminPlusTourSteps" :finish-route="null" finish-button-text="GOT IT" />
  </div>
</template>

<script>
import CustomOnboarding from "@/components/onboarding/CustomOnboarding.vue";
import { buildCalendarAdminPlusTourSteps } from "@/components/onboarding/calendarAdminPlusTourSteps";
import CalendarTab from './CalendarTab.vue';
import AcademyEventsTab from './AcademyEventsTab.vue';
import ScheduleTab from './ScheduleTab.vue';
import ConfigurationTab from './ConfigurationTab.vue';
import ViewCalendarHeader from '../../components/plus/calendar/ViewCalendarHeader.vue';
import CalendarAdminPlusScheduleClassDialog from '../../components/plus/calendar/CalendarAdminPlusScheduleClassDialog.vue';
import Api from '@/services/Api';
import Api2 from '@/services/Api2';
import PlusButton from '@/components/plus/PlusButton.vue';

export default {
  name: "ViewCalendarAdminPlus",
  components: {
    CalendarTab,
    AcademyEventsTab,
    ScheduleTab,
    ConfigurationTab,
    ViewCalendarHeader,
    CalendarAdminPlusScheduleClassDialog,
    CustomOnboarding,
    PlusButton,
  },
  data() {
    return {
      activeTab: 0,
      urlTab: this.$route.params.tabId,
      calendarLoaded: false,
      academyEventsLoaded: false,
      scheduleLoaded: false,
      configLoaded: false,
      loadingInitial: true,
      isEmptyState: false
    };
  },
  computed: {
    calendarAdminPlusTourSteps() {
      return buildCalendarAdminPlusTourSteps(this);
    },
  },
  created() {
    this.fetchCalendarIndexInitial();
  },
  mounted() {
    if (this.urlTab) {
      try {
        const n = new Number(this.urlTab);
        this.activeTab = n.valueOf();
      } catch (err) {
        console.log('Invalid tab: ', this.urlTab);
      }
    }
    this.tryStartCalendarAdminPlusTour();
  },
  watch: {
    async "$route.fullPath"() {
      await this.$nextTick();
      this.tryStartCalendarAdminPlusTour();
    },
    '$route'(to) {
      if (to.params.tabId !== undefined) {
        this.activeTab = parseInt(to.params.tabId);
      }
    }
  },
  methods: {
    stripCalendarAdminPlusTourQuery() {
      if (this.$route.query.tour !== "true") {
        return;
      }
      const query = { ...this.$route.query };
      delete query.tour;
      this.$router.replace({
        path: this.$route.path,
        query
      }).catch(() => { });
    },
    async tryStartCalendarAdminPlusTour() {
      if (this._calendarAdminPlusTourLaunching) {
        return;
      }
      const isCalendarRoute =
        this.$route.name === "calendar-adminplus" ||
        this.$route.name === "calendar-adminplus-tab";
      if (!isCalendarRoute || this.$route.query.tour !== "true") {
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));

      this._calendarAdminPlusTourLaunching = true;
      try {
        const appTour = this.$tours && this.$tours["custom-onboarding"];
        if (appTour && typeof appTour.stop === "function") {
          appTour.stop();
        }
        this.$emit("expand-nav-for-tour");
        await this.$nextTick();
        const delayMs = 500;
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        const onboarding = this.$refs.calendarAdminPlusTour;
        if (onboarding && typeof onboarding.start === "function") {
          await onboarding.start();
        }
        this.stripCalendarAdminPlusTourQuery();
      } finally {
        this._calendarAdminPlusTourLaunching = false;
      }
    },
    fetchCalendarIndexInitial() {
      const academy = this.$store.state.managedAcademy;
      const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      Promise.all([
        Api2().get('calendar/admin/academy/' + academy + '/events'),
        Api().get('admins/academy/' + academy + '/schedule')
      ])
        .then(([evRes, schRes]) => {
          const hasEvents = evRes.data && evRes.data.events && evRes.data.events.length > 0;
          const sch = schRes.data && schRes.data.schedule;
          const hasSchedule =
            sch && dayKeys.some((d) => sch[d] && sch[d].length > 0);
          this.isEmptyState = !hasEvents && !hasSchedule;
        })
        .catch(() => {
          this.isEmptyState = false;
        })
        .finally(() => {
          this.loadingInitial = false;
        });
    },
    openCalendarClassDialog() {
      this.$refs.scheduleClassDialog && this.$refs.scheduleClassDialog.openAdd();
    },
    onScheduleClassDialogSaved() {
      if (this.$refs.calendarTab && typeof this.$refs.calendarTab.loadCalendar === 'function') {
        this.$refs.calendarTab.loadCalendar();
      }
      if (this.$refs.scheduleTab && typeof this.$refs.scheduleTab.refreshSchedule === 'function') {
        this.$refs.scheduleTab.refreshSchedule();
      }
      if (this.isEmptyState) {
        this.fetchCalendarIndexInitial();
      }
    },
    clickTab(tab) {
      if (tab != this.activeTab) {
        this.navigateTo({
          name: "calendar-adminplus-tab",
          params: { tabId: tab },
        });
      }
    }
  }
}
</script>
